import { useEffect, useState } from 'react'

/**
 * Is this a phone-width screen? (24 Sep 2026)
 *
 * 639px is Tailwind's `sm` breakpoint minus one, so this hook and every
 * `sm:` class in the app agree about where a phone stops being a phone. If
 * that ever drifts, a screen will be laid out for one and behave like the
 * other, which is worse than either.
 *
 * A hook rather than a CSS class because the call sheet genuinely renders a
 * DIFFERENT component on a phone — a read view instead of a builder — and
 * hiding one of two mounted components with `sm:hidden` would mean loading
 * the same call sheet twice on every open.
 *
 * It listens, so rotating the phone or dragging a desktop window across the
 * breakpoint switches live rather than waiting for a reload.
 */
const PHONE = '(max-width: 639px)'

export function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(PHONE).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const query = window.matchMedia(PHONE)
    const onChange = (event: MediaQueryListEvent) => setIsPhone(event.matches)
    /* Safari only got addEventListener on MediaQueryList in 14, and this app
       is used on whatever phone is in someone's pocket. */
    if (query.addEventListener) {
      query.addEventListener('change', onChange)
      return () => query.removeEventListener('change', onChange)
    }
    query.addListener(onChange)
    return () => query.removeListener(onChange)
  }, [])

  return isPhone
}
